import React, { useEffect, useRef } from 'react';

const FloatingHeart = ({ style }) => (
  <div style={{ position: 'absolute', animation: `float ${3 + Math.random() * 2}s ease-in-out infinite`, ...style }}>
    {'💕'}
  </div>
);

export default function HeroSection() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 0.5,
      dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.4,
      alpha: Math.random() * 0.7 + 0.3,
    }));

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,107,157,${p.alpha})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section id="hero" style={{
      minHeight: '100vh',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: 'radial-gradient(ellipse at 50% 0%, rgba(168,85,247,0.2) 0%, rgba(13,10,20,0) 70%), radial-gradient(ellipse at 80% 80%, rgba(255,107,157,0.15) 0%, transparent 60%), #0d0a14',
    }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

      {/* Floating hearts */}
      {[
        { top: '15%', left: '8%', fontSize: 24, animationDelay: '0s' },
        { top: '25%', right: '10%', fontSize: 18, animationDelay: '0.5s' },
        { top: '65%', left: '5%', fontSize: 16, animationDelay: '1s' },
        { top: '75%', right: '8%', fontSize: 22, animationDelay: '1.5s' },
        { top: '45%', right: '4%', fontSize: 14, animationDelay: '0.8s' },
      ].map((s, i) => (
        <div key={i} style={{ position: 'absolute', ...s, animation: `float ${3 + i * 0.5}s ease-in-out infinite`, animationDelay: s.animationDelay, zIndex: 1 }}>
          {['💕','✨','🌸','💫','🦋'][i]}
        </div>
      ))}

      {/* Glowing orbs */}
      <div style={{
        position: 'absolute', width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)',
        top: '-100px', right: '-100px', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,107,157,0.1) 0%, transparent 70%)',
        bottom: '-50px', left: '-50px', pointerEvents: 'none',
      }} />

      {/* CONTENT */}
      <div style={{
        position: 'relative', zIndex: 2, textAlign: 'center',
        padding: '120px 24px 60px', maxWidth: 800, margin: '0 auto',
      }}>
        {/* Illustration */}
        <div style={{
          width: 180, height: 180, margin: '0 auto 40px',
          position: 'relative',
          animation: 'float 4s ease-in-out infinite',
        }}>
          <svg viewBox="0 0 180 180" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
            {/* Glow circle */}
            <circle cx="90" cy="90" r="80" fill="url(#heroGrad)" opacity="0.15" />
            <circle cx="90" cy="90" r="75" fill="none" stroke="url(#heroStroke)" strokeWidth="1.5" strokeDasharray="8 4" />
            {/* Boy */}
            <circle cx="62" cy="65" r="18" fill="#2d1f4a" />
            <circle cx="62" cy="65" r="16" fill="#3d2a6a" />
            <circle cx="55" cy="61" r="4" fill="#a855f7" />
            <circle cx="69" cy="61" r="4" fill="#a855f7" />
            <circle cx="55" cy="61" r="2" fill="#1a0a2e" />
            <circle cx="69" cy="61" r="2" fill="#1a0a2e" />
            <path d="M56,72 Q62,77 68,72" stroke="#ff9db5" strokeWidth="2" fill="none" strokeLinecap="round" />
            <rect x="44" y="83" width="36" height="40" rx="12" fill="#3d2a6a" />
            <rect x="44" y="83" width="36" height="18" rx="8" fill="#a855f7" />
            {/* Girl */}
            <circle cx="118" cy="65" r="18" fill="#2d1f4a" />
            <circle cx="118" cy="65" r="16" fill="#3d2a6a" />
            <circle cx="111" cy="61" r="4" fill="#ff6b9d" />
            <circle cx="125" cy="61" r="4" fill="#ff6b9d" />
            <circle cx="111" cy="61" r="2" fill="#1a0a2e" />
            <circle cx="125" cy="61" r="2" fill="#1a0a2e" />
            <path d="M112,72 Q118,78 124,72" stroke="#ff9db5" strokeWidth="2" fill="none" strokeLinecap="round" />
            {/* Girl hair */}
            <path d="M102,58 Q100,48 110,44 Q118,40 126,44 Q136,48 134,58" fill="#1a0a2e" />
            <path d="M102,58 Q98,68 102,78" stroke="#1a0a2e" strokeWidth="8" strokeLinecap="round" fill="none" />
            <path d="M134,58 Q138,68 134,78" stroke="#1a0a2e" strokeWidth="8" strokeLinecap="round" fill="none" />
            <rect x="100" y="83" width="36" height="40" rx="12" fill="#3d2a6a" />
            <rect x="100" y="83" width="36" height="18" rx="8" fill="#ff6b9d" />
            {/* Heart between them */}
            <path d="M87,95 C87,90 82,86 82,92 C82,97 87,101 87,101 C87,101 92,97 92,92 C92,86 87,90 87,95 Z" fill="url(#heartGrad)" style={{ animation: 'heartbeat 1.5s ease-in-out infinite' }} />
            {/* Stars */}
            <circle cx="30" cy="30" r="2" fill="#ff6b9d" />
            <circle cx="150" cy="30" r="2" fill="#a855f7" />
            <circle cx="25" cy="130" r="1.5" fill="#38bdf8" />
            <circle cx="155" cy="120" r="1.5" fill="#fb923c" />
            <defs>
              <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff6b9d" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
              <linearGradient id="heroStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff6b9d" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
              <linearGradient id="heartGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff6b9d" />
                <stop offset="100%" stopColor="#d94f7a" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="section-badge" style={{ margin: '0 auto 20px', display: 'inline-flex' }}>
          <span>✨</span> Our Love Story
        </div>

        <h1 style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: 'clamp(40px, 8vw, 80px)',
          fontWeight: 900, lineHeight: 1.1,
          marginBottom: 24,
          background: 'linear-gradient(135deg, #fff9fb 0%, #ff6b9d 40%, #a855f7 70%, #38bdf8 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: 'shimmer 4s linear infinite',
        }}>Our Timeline</h1>

        <p style={{
          fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(16px, 2.5vw, 20px)',
          color: '#9b7ba8', lineHeight: 1.8, maxWidth: 500, margin: '0 auto 40px',
        }}>
          Every moment we shared, every laugh, every conversation — our beautiful journey from strangers to something so much more. 💕
        </p>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="#first-talk" className="btn-primary">
            <span>Start the Journey</span>
            <span>→</span>
          </a>
          <a href="#countdown" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '14px 32px',
            background: 'transparent',
            color: '#e2c8d8',
            fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 15,
            border: '1px solid rgba(255,107,157,0.4)',
            borderRadius: 50, textDecoration: 'none',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,157,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <span>⏳</span> Countdown to Eid
          </a>
        </div>

        {/* Stats row */}
        <div style={{
          display: 'flex', gap: 32, justifyContent: 'center', marginTop: 60,
          flexWrap: 'wrap',
        }}>
          {[
            { val: '10+', label: 'Moments' },
            { val: '∞', label: 'Memories' },
            { val: '1', label: 'Story' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: 'Poppins, sans-serif', fontWeight: 800,
                fontSize: 32, lineHeight: 1,
                background: 'linear-gradient(135deg, #ff6b9d, #a855f7)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>{s.val}</div>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: 13, color: '#9b7ba8', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

       
    </section>
  );
}